import { useRef, useState } from "react";
import moment from "moment";
import customAxios from "../../../../utils/customAxios";
import { LONG_DISTANCE_FEE_TYPE } from "../constants";
import { toValidLongDistanceFee } from "../utils";

export function useBookingSubmission({
    id,
    requestId,
    formatedTravelDate,
    travelDate,
    pickupTime,
    pickupLocationData,
    dropoffLocationData,
    airportDropoffLocations,
    transferType,
    passengers,
    luggage,
    duration,
    travelArea,
    transferFlags,
    meetAssistS,
    vehicles,
    availableVehicleTypes,
    childSeatAllocations,
    passengerName,
    passengerTitle,
    prefixPhoneNumber,
    countryCode,
    countriesData,
    phoneNumber,
    message,
    pickupFlightNo,
    dropoffFlightNo,
    pickupTerminal,
    dropoffTerminal,
    pickupSignHonorific,
    pickupSign,
    basePriceType,
    garageType,
    discountType,
    discountValue,
    longDistanceFeeType,
    longDistanceFeeValue,
    departureGarageId,
    returnGarageId,
}) {
    const [isSimulatingReview, setIsSimulatingReview] = useState(false);
    const [isSubmittingModification, setIsSubmittingModification] = useState(false);
    const [reviewSimulationData, setReviewSimulationData] = useState(null);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [errorModalText, setErrorModalText] = useState("");
    const reviewedPayloadRef = useRef(null);

    const handleCloseErrorModal = () => {
        setShowErrorModal(false);
        setErrorModalText("");
    };

    const buildSimulationPayload = () => {
        const pickupLocationId = pickupLocationData?.place_id || pickupLocationData?.value || "";
        const destinationLocations =
            transferType === "charter"
                ? [dropoffLocationData]
                : airportDropoffLocations.map((destination) => destination?.data).filter(Boolean);
        const destinationIds = destinationLocations
            .map((destination) => destination?.place_id || destination?.value)
            .filter(Boolean);

        const paxList = [
            { paxtype: "Adult", paxcount: Number(passengers?.adult || 0) },
            { paxtype: "Child", paxcount: Number(passengers?.child || 0) },
            { paxtype: "Toddler", paxcount: Number(passengers?.toddler || 0) },
            { paxtype: "Baby", paxcount: Number(passengers?.baby || 0) },
        ];

        const luggageList = [
            { luggagetype: "CHECK-IN SIZE", luggagecount: Number(luggage?.checkin || 0) },
            { luggagetype: "CABIN SIZE", luggagecount: Number(luggage?.cabin || 0) },
        ];

        const selectedVehicles = (vehicles || []).filter(
            (vehicle) => vehicle?.selectedVehicle || vehicle?.selectedVehicleId
        );

        const bookingInfo = selectedVehicles.map((vehicle, index) => {
            const selectedVehicleType = (availableVehicleTypes || []).find(
                (vehicleType) =>
                    vehicleType?.typename === vehicle?.selectedVehicle
            );
            const childSeatAllocation = (childSeatAllocations || [])[index] || {};
            const vehicleTypeName = vehicle?.selectedVehicle || selectedVehicleType?.typename || "";
            const vehicleTypeId = selectedVehicleType?._id || vehicle?.selectedVehicleId || "";

            return {
                vehicleTypeName,
                vehicleTypeID: vehicleTypeId,
                pax: paxList,
                luggage: luggageList,
                childSeat: [
                    {
                        childSeatType: "Infant",
                        childSeatCount: Number(childSeatAllocation?.infant || 0),
                    },
                    {
                        childSeatType: "Toddler",
                        childSeatCount: Number(childSeatAllocation?.toddler || 0),
                    },
                    {
                        childSeatType: "Child",
                        childSeatCount: Number(childSeatAllocation?.child || 0),
                    },
                ],
            };
        });

        const selectedCountry = (countriesData || []).find(
            (country) => country?.code === countryCode
        );
        const pickupTime24 = pickupTime
            ? moment(pickupTime, ["h:mm A", "HH:mm"], true).format("HH:mm")
            : "";
        const isManualLongDistanceFee = longDistanceFeeType === LONG_DISTANCE_FEE_TYPE.MANUAL;

        return {
            bookingId: id,
            travelDate:
                formatedTravelDate ||
                (travelDate ? moment(travelDate).format("YYYY-MM-DD") : ""),
            pickupTime: pickupTime24,
            pickupLocation: pickupLocationId,
            destinations: destinationIds,
            pax: {
                adult: Number(passengers?.adult || 0),
                child: Number(passengers?.child || 0),
                toddler: Number(passengers?.toddler || 0),
                baby: Number(passengers?.baby || 0),
            },
            luggage: {
                cabinSize: Number(luggage?.cabin || 0),
                checkinSize: Number(luggage?.checkin || 0),
            },
            bookingType: transferType === "charter" ? "byTheHour" : "specifiedDestination",
            duration: transferType === "charter" ? duration?.value || "" : undefined,
            travelArea: transferType === "charter" ? travelArea || null : undefined,
            isAirportTransfer: Boolean(transferFlags?.isAirportTransfer),
            isFromAirport: Boolean(transferFlags?.isFromAirport),
            isToAirport: Boolean(transferFlags?.isToAirport),
            isStationTransfer: Boolean(transferFlags?.isStationTransfer),
            isFromStation: Boolean(transferFlags?.isFromStation),
            isToStation: Boolean(transferFlags?.isToStation),
            meetAssist: meetAssistS,
            isManyPeople: false,
            bookingInfo,
            passengerName,
            passengerPrefixNumber: prefixPhoneNumber,
            passengerCountryCode: countryCode,
            passengerCountry: selectedCountry?.name || "",
            passengerPhoneNumber: phoneNumber,
            passengerPronounced: passengerTitle,
            noteList: [{ isAdmin: false, note: message || "" }],
            pickupFlightNo: pickupFlightNo || undefined,
            dropoffFlightNo: dropoffFlightNo || undefined,
            pickupTerminal: pickupTerminal || undefined,
            dropoffTerminal: dropoffTerminal || undefined,
            pickupSignHonorific: pickupSignHonorific,
            pickupSign: pickupSign,
            useCustomGarage: garageType === "custom",
            departureGarage: garageType === "custom" ? departureGarageId || "" : "",
            returnGarage: garageType === "custom" ? returnGarageId || "" : "",
            baseprice: basePriceType === "outsideTokyo" ? "outsideTokyo" : "default",
            useDiscount: discountType === "applicable",
            discount: discountType === "applicable" ? Number(discountValue || 0) : 0,
            // When "manual", the server uses this fee instead of calculating it from the distance.
            useManualLongDistanceFee: isManualLongDistanceFee,
            manualLongDistanceFee: isManualLongDistanceFee ? toValidLongDistanceFee(longDistanceFeeValue) : 0,
        };
    };

    const handleReviewChanges = async () => {
        try {
            setIsSimulatingReview(true);
            const payload = buildSimulationPayload();
            reviewedPayloadRef.current = payload;
            const response = await customAxios.post(
                "/api/admin/request-change-booking/edit-booking/simulation",
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            const simulationData = response?.data?.data || response?.data || null;
            setReviewSimulationData(simulationData);
        } catch (error) {
            console.error(
                "Edit booking simulation failed:",
                error?.response?.data || error
            );
            const errorMessage = error?.response?.data?.message || error?.response?.data?.error || "An error occurred while simulating the booking changes. Please try again.";
            setErrorModalText(errorMessage);
            setShowErrorModal(true);
        } finally {
            setIsSimulatingReview(false);
        }
    };

    const handleSubmitModificationRequest = async (options = {}) => {
        try {
            setIsSubmittingModification(true);
            const payload = reviewedPayloadRef.current || buildSimulationPayload();
            const submitPayload = {
                ...payload,
                ...(requestId ? { updateRequestId: requestId } : {}),
                ...(options?.saveAsDraft ? { isDraft: true, saveAsDraft: true, status: "DRAFT" } : {}),
            };
            await customAxios.post(
                "/api/admin/request-change-booking/edit-booking/submit",
                submitPayload,
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            return true;
        } catch (error) {
            const errorData = error?.response?.data;

            console.error("Edit booking submit failed:", errorData || error);

            // A failed charge means nothing was saved, so the admin has to be told
            // rather than left thinking the edit went through.
            const errorMessage =
                errorData?.msg ||
                errorData?.message ||
                errorData?.error ||
                "An error occurred while submitting the booking changes. Please try again.";

            setErrorModalText(errorMessage);
            setShowErrorModal(true);

            return false;
        } finally {
            setIsSubmittingModification(false);
        }
    };

    return {
        isSimulatingReview,
        isSubmittingModification,
        reviewSimulationData,
        setReviewSimulationData,
        handleReviewChanges,
        handleSubmitModificationRequest,
        showErrorModal,
        errorModalText,
        handleCloseErrorModal,
    };
}
